import React, { useEffect, useState } from 'react';
import { IAnalyticsFilters, IAnalyticsRevenueResponse } from '@/interfaces/Dashboard/dashboard.interface';
import { AnalyticsExportButton } from './AnalyticsExportButton';
import { CommonCard } from '@/components/common/CommonCard';
import { SectionCard } from '@/components/common/SectionCard';
import { formatRand, formatChartDate } from '@/utility/helper';
import { DollarSign, TrendingUp, CreditCard, Percent, ArrowDownToLine, Receipt, FileText, Gift, RotateCcw, Store } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend } from 'recharts';
import { CommonCardSkeleton, LineChartSkeleton, HorizontalBarChartSkeleton, DonutChartSkeleton } from '@/components/ui/Loaders/DashboardSkeletons';
import { EmptyChart } from '@/components/common/EmptyChart';
import { NoDataFound } from '@/components/common/NoDataFound';
import CommonTable from '@/components/common/CommonTable';

const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'];

interface RevenueSectionProps {
    filters: IAnalyticsFilters;
    data: IAnalyticsRevenueResponse['data'] | null;
    isLoading: boolean;
}


export const RevenueSection: React.FC<RevenueSectionProps> = ({ filters, data, isLoading }) => {

    if (isLoading) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="flex justify-between items-center mb-4">
                    <div className="h-6 w-32 bg-gray-200 rounded"></div>
                    <div className="h-8 w-24 bg-gray-200 rounded"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Array.from({ length: 9 }).map((_, i) => <CommonCardSkeleton key={i} />)}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <LineChartSkeleton />
                    <LineChartSkeleton />
                </div>
            </div>
        );
    }

    const kpis = data?.kpis || {} as any;
    const charts = data?.charts || {} as any;
    const tables = data?.tables || {} as any;

    const kpiCards = [
        { label: 'Total Revenue', value: formatRand(kpis.totalRevenue), icon: <DollarSign />, color: '#10B981' },
        { label: 'GMV', value: formatRand(kpis.gmv), icon: <TrendingUp />, color: '#3B82F6' },
        { label: 'Net Revenue', value: formatRand(kpis.netRevenue), icon: <CreditCard />, color: '#F59E0B' },
        { label: 'Platform Commission', value: formatRand(kpis.platformCommission), icon: <Percent />, color: '#8B5CF6' },
        { label: 'Delivery Fee Collected', value: formatRand(kpis.deliveryFeeCollected), icon: <ArrowDownToLine />, color: '#EC4899' },
        { label: 'Platform Fee Collected', value: formatRand(kpis.platformFeeCollected), icon: <Receipt />, color: '#14B8A6' },
        { label: 'Taxes Collected', value: formatRand(kpis.taxesCollected), icon: <FileText />, color: '#F97316' },
        { label: 'Total Discounts', value: formatRand(kpis.totalDiscounts), icon: <Gift />, color: '#6366F1' },
        { label: 'Refund Amount', value: formatRand(kpis.refundAmount), icon: <RotateCcw />, color: '#EF4444' },
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-foreground">Revenue & GMV</h2>
                <AnalyticsExportButton
                    exportUrl="/reports/analytics/export?type=revenue"
                    fileName="revenue_report.xlsx"
                    filters={filters}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {kpiCards.map((card, i) => (
                    <CommonCard key={i} label={card.label} value={card.value} icon={card.icon} iconColor={card.color} />
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-1 gap-4">
                <SectionCard title="Revenue Trend" icon={<TrendingUp />}>
                    <div className="h-[300px] w-full">
                        {!charts.revenueTrend || charts.revenueTrend.length === 0 ? (
                            <EmptyChart height="h-[300px]" />
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={charts.revenueTrend}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} tickFormatter={formatChartDate} interval={0} angle={-45} textAnchor="end" height={60} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} tickFormatter={(val) => `R${val}`} />
                                    <Tooltip
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                        formatter={(value: number) => [formatRand(value), 'Revenue']}
                                    />
                                    <Area type="monotone" dataKey="revenue" stroke="#10B981" fill="#10B981" fillOpacity={0.2} strokeWidth={3} />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </SectionCard>

                <SectionCard title="GMV Trend" icon={<TrendingUp />}>
                    <div className="h-[300px] w-full">
                        {!charts.revenueTrend || charts.revenueTrend.length === 0 ? (
                            <EmptyChart height="h-[300px]" />
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={charts.revenueTrend}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} tickFormatter={formatChartDate} interval={0} angle={-45} textAnchor="end" height={60} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} tickFormatter={(val) => `R${val}`} />
                                    <Tooltip
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                        formatter={(value: number) => [formatRand(value), 'GMV']}
                                    />
                                    <Area type="monotone" dataKey="gmv" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.2} strokeWidth={3} />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </SectionCard>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-1 gap-4">
                <SectionCard title="Revenue by Store" icon={<Store />}>
                    <div className="h-[300px] w-full">
                        {!charts.revenueByStore || charts.revenueByStore.length === 0 ? (
                            <EmptyChart height="h-[300px]" />
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={charts.revenueByStore} layout="vertical" margin={{ left: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E5E7EB" />
                                    <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} tickFormatter={(val) => `R${val/1000}k`} />
                                    <YAxis type="category" dataKey="storeName" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#374151', fontWeight: 500 }} width={120} />
                                    <Tooltip
                                        cursor={{ fill: '#F3F4F6' }}
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                        formatter={(value: number) => [formatRand(value), 'Revenue']}
                                    />
                                    <Bar dataKey="revenue" fill="#10B981" radius={[0, 4, 4, 0]} barSize={24} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </SectionCard>

                <SectionCard title="Revenue by Category" icon={<FileText />}>
                    <div className="h-[300px] w-full flex items-center justify-center">
                        {!charts.revenueByCategory || charts.revenueByCategory.length === 0 ? (
                            <EmptyChart height="h-[300px]" />
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={charts.revenueByCategory}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={70}
                                        outerRadius={100}
                                        paddingAngle={5}
                                        dataKey="revenue"
                                        nameKey="category"
                                    >
                                        {charts.revenueByCategory.map((_, index) => (
                                            <Cell key={`cell-\${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                        formatter={(value: number) => [formatRand(value), 'Revenue']}
                                    />
                                    <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </SectionCard>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <SectionCard title="Top Revenue Stores" className="overflow-x-auto">
                    {!tables.topRevenueStores || tables.topRevenueStores.length === 0 ? (
                        <div className="py-8"><NoDataFound title="No Data Found" description="There is no data available for this table." /></div>
                    ) : (
                        <CommonTable
                            columns={[
                                { key: 'storeName', header: 'Store Name', className: 'font-medium text-foreground' },
                                { key: 'orders', header: 'Orders', className: 'text-right', headerClassName: 'text-right' },
                                { key: 'commission', header: 'Commission', render: (row: any) => formatRand(row.commission), className: 'text-right', headerClassName: 'text-right' },
                                { key: 'revenue', header: 'Revenue', render: (row: any) => formatRand(row.revenue), className: 'text-right font-bold text-success', headerClassName: 'text-right' }
                            ]}
                            data={tables.topRevenueStores}
                        />
                    )}
                </SectionCard>

                <SectionCard title="Top Revenue Products" className="overflow-x-auto">
                    {!tables.topRevenueProducts || tables.topRevenueProducts.length === 0 ? (
                        <div className="py-8"><NoDataFound title="No Data Found" description="There is no data available for this table." /></div>
                    ) : (
                        <CommonTable
                            columns={[
                                { key: 'productName', header: 'Product Name', className: 'font-medium text-foreground' },
                                { key: 'unitsSold', header: 'Units Sold', className: 'text-right', headerClassName: 'text-right' },
                                { key: 'revenue', header: 'Revenue', render: (row: any) => formatRand(row.revenue), className: 'text-right font-bold text-success', headerClassName: 'text-right' }
                            ]}
                            data={tables.topRevenueProducts}
                        />
                    )}
                </SectionCard>
            </div>
        </div>
    );
};
